<template>
	<div class="field-drawer-trigger" @click="openDrawer" :class="{ disabled: disabled }">
		<div v-if="selectedLogo" class="trigger-logo">
			<img :src="selectedLogo" :alt="displayValue" @error="handleLogoError" />
		</div>
		<div class="trigger-content" :class="{ 'has-value': modelValue }">
			<span class="trigger-label">{{ label }} {{ required && !disabled ? '*' : '' }}</span>
			<span v-if="modelValue" class="trigger-value">{{ displayValue }}</span>
		</div>
		<v-icon v-if="modelValue && clearable" class="trigger-clear" @click.stop="clearValue">mdi-close</v-icon>
		<v-icon v-else-if="!disabled" class="trigger-chevron">mdi-chevron-down</v-icon>
	</div>

	<DrawerPanel
		ref="drawerPanel"
		:title="label"
		:divider="false"
		:close="true"
		openHeight="85%"
		closedHeight="0px"
		bottom="0"
		backgroundColor="#fff"
		openBackgroundColor="#fff"
		:blurBackground="true"
		:keyboard="searchFocused"
		@closed="onDrawerClosed"
	>
		<div class="drawer-content">
			<div class="subtitle-section">
				<p class="drawer-title">{{ subtitle || 'Choose a ' + label.toLowerCase() }}</p>
				<p v-if="notListedText" class="drawer-subtitle">{{ notListedText }}</p>
			</div>

			<div class="search-container" v-if="items.length > 5">
				<v-icon class="search-icon">mdi-magnify</v-icon>
				<input
					type="text"
					v-model="searchQuery"
					placeholder="Search"
					class="search-input"
					ref="searchInput"
					@focus="onSearchFocus"
					@blur="onSearchBlur"
				/>
				<v-icon v-if="searchQuery" class="search-clear" @click="searchQuery = ''">mdi-close</v-icon>
			</div>

			<div class="items-list">
				<div
					v-for="item in filteredItems"
					:key="getItemValue(item)"
					class="item-card"
					:class="{ selected: getItemValue(item) === modelValue }"
					@click="selectItem(item)"
				>
					<div v-if="showAvatars" class="item-logo">
						<img v-if="getItemLogo(item)" :src="getItemLogo(item)" :alt="getItemText(item)" @error="handleLogoError" />
						<div v-else class="logo-placeholder">
							<span>{{ getItemText(item).charAt(0).toUpperCase() }}</span>
						</div>
					</div>
					<span class="item-text">{{ getItemText(item) }}</span>
					<v-icon v-if="getItemValue(item) === modelValue" class="item-check">mdi-check</v-icon>
				</div>
				<div v-if="filteredItems.length === 0" class="no-results">
					No results found
				</div>
			</div>
		</div>
	</DrawerPanel>
</template>

<script>
import DrawerPanel from './DrawerPanel.vue';
import Helpers from '../../mixins/Helpers.js';

export default {
	name: 'FieldDrawer',
	mixins: [Helpers],
	components: { DrawerPanel },
	props: {
		modelValue: {
			type: [String, Number, Object],
			default: null
		},
		label: {
			type: String,
			required: true
		},
		items: {
			type: Array,
			default: () => []
		},
		subtitle: {
			type: String,
			default: null
		},
		notListedText: {
			type: String,
			default: null
		},
		required: {
			type: Boolean,
			default: false
		},
		clearable: {
			type: Boolean,
			default: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		showAvatars: {
			type: Boolean,
			default: true
		},
		theme: {
			type: String,
			default: 'light'
		}
	},
	emits: ['update:modelValue'],
	data() {
		return {
			searchQuery: '',
			searchFocused: false
		}
	},
	computed: {
		filteredItems() {
			if (!this.searchQuery) {
				return this.items;
			}
			const query = this.searchQuery.toLowerCase();
			return this.items.filter(item => {
				const text = this.getItemText(item).toLowerCase();
				return text.includes(query);
			});
		},
		displayValue() {
			const item = this.items.find(i => this.getItemValue(i) === this.modelValue);
			return item ? this.getItemText(item) : this.modelValue;
		},
		selectedLogo() {
			if (!this.showAvatars || !this.modelValue) return null;
			const item = this.items.find(i => this.getItemValue(i) === this.modelValue);
			return item ? this.getItemLogo(item) : null;
		}
	},
	methods: {
		getItemText(item) {
			if (typeof item === 'string') return item;
			return item.text || item.name || item.label || '';
		},
		getItemValue(item) {
			if (typeof item === 'string') return item;
			return item.value !== undefined ? item.value : (item.name || item.text || item.label);
		},
		getItemLogo(item) {
			if (typeof item === 'string') return this._getBrandLogoUrl(item);
			if (item.logoUri || item.logo || item.icon) return item.logoUri || item.logo || item.icon;
			const name = this.getItemText(item);
			if (name) return this._getBrandLogoUrl(name);
			return null;
		},
		handleLogoError(event) {
			event.target.src = require('@/assets/round1.png');
		},
		openDrawer() {
			if (this.disabled) return;
			this.searchQuery = '';
			this.$refs.drawerPanel.openDrawer();
		},
		onSearchFocus() {
			this.searchFocused = true;
		},
		onSearchBlur() {
			setTimeout(() => {
				this.searchFocused = false;
			}, 500);
		},
		onDrawerClosed() {
			this.searchQuery = '';
		},
		selectItem(item) {
			const value = this.getItemValue(item);
			this.$emit('update:modelValue', value);
			setTimeout(() => {
				this.$refs.drawerPanel.closeDrawer();
			}, 200);
		},
		clearValue() {
			this.$emit('update:modelValue', null);
		}
	}
}
</script>

<style scoped>
.field-drawer-trigger {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px;
	background-color: var(--input-bg);
	border-radius: 8px;
	margin-bottom: 16px;
	min-height: 56px;
}

.field-drawer-trigger.disabled {
	opacity: 1;
}

.trigger-logo {
	width: 28px;
	height: 28px;
	flex-shrink: 0;
	margin-right: 12px;
}

.trigger-logo img {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	object-fit: cover;
}

.trigger-content {
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
}

.trigger-label {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.trigger-content.has-value .trigger-label {
	font-size: 11px;
}

.trigger-value {
	font-size: 16px;
	color: var(--background-color-primary);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.trigger-clear,
.trigger-chevron {
	color: var(--light-text-secondary);
}

/* Drawer content styles */
.drawer-content {
	display: flex;
	flex-direction: column;
	gap: 20px;
	height: 100%;
}

.subtitle-section {
	display: flex;
	flex-direction: column;
}

.drawer-title {
	font-size: 15px;
	font-weight: bold;
	color: var(--background-color-primary);
	margin: 0;
	line-height: 1.47;
}

.drawer-subtitle {
	font-size: 13px;
	color: var(--background-color-primary);
	margin: 0;
	line-height: 1.23;
}

.search-container {
	display: flex;
	align-items: center;
	background-color: var(--input-bg);
	border-radius: 8px;
	padding: 10px 12px;
	gap: 8px;
	height: 40px;
}

.search-icon {
	color: var(--background-color-primary);
	font-size: 20px;
}

.search-input {
	flex: 1;
	border: none;
	background: none;
	font-size: 14px;
	outline: none;
	color: var(--background-color-primary);
	-webkit-appearance: none;
	appearance: none;
}

.search-input:focus {
	border: none;
	outline: none;
	box-shadow: none;
}

.search-container:focus-within {
	outline: none;
	box-shadow: none;
	border: none;
}

.search-input::placeholder {
	color: var(--background-color-primary);
}

.search-clear {
	color: var(--light-text-secondary);
	font-size: 18px;
}

.items-list {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin: 0 -20px -150px -20px;
	padding: 0 20px 40px 20px;
}

.item-card {
	display: flex;
	align-items: center;
	padding: 12px 14px;
	background-color: var(--input-bg);
	border-radius: 10px;
	gap: 16px;
	min-height: 50px;
}

.item-card.selected {
	background-color: var(--input-bg);
}

.item-logo {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.item-logo img {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	object-fit: cover;
	border: 0.5px solid var(--background-color-main);
	background: var(--background-color);
}

.logo-placeholder {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: var(--background-color-primary);
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--background-color);
	font-size: 14px;
	font-weight: bold;
}

.item-text {
	flex: 1;
	font-size: 15px;
	color: var(--background-color-primary);
	line-height: 1.47;
}

.item-check {
	color: var(--positive-amount);
	font-size: 20px;
}

.no-results {
	text-align: center;
	padding: 40px 20px;
	color: var(--dark-text-secondary);
	font-size: 15px;
}
</style>
